import { useLazyQuery } from "@apollo/client";
import DeleteIcon from "@mui/icons-material/Delete";
import Download from "@mui/icons-material/Download";
import Edit from "@mui/icons-material/Edit";
import { Box, Button, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid-premium";
import {
  AddEditDocumentDialog,
  AddEditDocumentDialogState,
  defaultAddEditDocumentDialogState,
  EditMode,
  FormState,
} from "components/AddEditDocumentDialog";
import { useAlerts } from "components/Alerts/AlertProvider";
import { DataGrid } from "components/DataGrid";
import { getStaffFacingActivityTitle } from "encamp-shared/src/activity/activityDisplayNames";
import { prettyPrintDocumentType } from "encamp-shared/src/utils/prettyPrintDocumentType";
import { useDeleteDocumentMutation } from "hooks/documents";
import GET_DOCUMENT_DOWNLOAD_LINK_QUERY from "queries/getDocumentDownloadLink";
import { useState, useEffect } from "react";
import { useReportDetails } from "../useReportDetails";
import { ConfirmDeleteDocuments } from "./ConfirmDeleteDocuments";

// Optional props for reusability with cohort data
interface DocumentListProps {
  documents?: any[];
  activities?: any[];
  tenantId?: string;
  reportId?: string;
  loading?: boolean;
  onRefetch?: () => void;
  refetchQueries?: string[];
  showAdvancedTooltips?: boolean;
  // For cohort mode - automatically transform activity documents
  cohortMode?: boolean;
  showPaper?: boolean;
  title?: string;
}

export function DocumentList({
  documents: externalDocuments,
  activities: externalActivities,
  tenantId: externalTenantId,
  reportId: externalReportId,
  loading: externalLoading,
  onRefetch: externalRefetch,
  refetchQueries: externalRefetchQueries,
  showAdvancedTooltips = false,
  cohortMode = false,
  showPaper = false,
  title,
}: DocumentListProps = {}) {
  const { data, refetch, loading: reportLoading } = useReportDetails();
  const report = data?.tierIIReport;

  // Use external props if provided, otherwise use report data
  let documents = externalDocuments ?? report?.documents ?? [];
  const activities =
    externalActivities ?? report?.currentWorkflow?.activities ?? [];
  const tenantId = externalTenantId ?? report?.tenantId ?? "";
  const reportId = externalReportId ?? report?.id;
  const loading = externalLoading ?? reportLoading;
  const handleRefetch = externalRefetch ?? refetch;
  const refetchQueriesList = externalRefetchQueries ?? [];

  // Transform cohort data if in cohort mode
  if (cohortMode && activities.length > 0) {
    documents = activities.flatMap(
      (activity) =>
        activity.documents?.map((doc: any) => ({
          ...doc.document,
          activities: [activity], // Add activity info for the Activity column
        })) || []
    );
  }

  const [deleteDocumentId, setDeleteDocumentId] = useState<string | null>(null);

  const [addEditDialogState, setAddEditDialogState] =
    useState<AddEditDocumentDialogState>(defaultAddEditDocumentDialogState);

  // Move the state update to useEffect to avoid updating state during render
  useEffect(() => {
    if (reportId) {
      setAddEditDialogState((state) => ({
        ...state,
        reportId: reportId,
        facilities: report?.facility?.id ? [{ id: report.facility.id }] : [],
      }));
    }
  }, [reportId, report?.facility?.id]);

  const [deleteDocument, { loading: deleting }] = useDeleteDocumentMutation({
    update(cache, { data }) {
      if (!data?.deleteDocument) return;
      const id = cache.identify(data.deleteDocument);
      cache.evict({ id });
    },
  });
  const alerts = useAlerts();

  const [getDocumentDownloadLink] = useLazyQuery(
    GET_DOCUMENT_DOWNLOAD_LINK_QUERY,
    {
      onCompleted: (data) => {
        if (!data?.getDocumentDownloadLink) {
          alerts.error("Error fetching download link");
          return;
        }
        window.open(data.getDocumentDownloadLink, "_blank");
      },
      onError: (e) => {
        alerts.error("Error fetching download link", e);
      },
    }
  );

  const columns: GridColDef[] = [
    {
      field: "title",
      headerName: "Title",
      flex: 1,
      editable: true,
      renderCell: showAdvancedTooltips
        ? ({ value }) => {
            if (!value) return null;
            return (
              <Tooltip title={value}>
                <span
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "block",
                  }}
                >
                  {value}
                </span>
              </Tooltip>
            );
          }
        : undefined,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      editable: true,
      renderCell: showAdvancedTooltips
        ? ({ value }) => {
            if (!value) return null;
            return (
              <Tooltip title={value}>
                <span
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "block",
                  }}
                >
                  {value}
                </span>
              </Tooltip>
            );
          }
        : undefined,
    },
    {
      field: "documentType",
      headerName: "Document Type",
      flex: showAdvancedTooltips ? 0.8 : 1,
      renderCell: showAdvancedTooltips
        ? ({ value }) => {
            if (!value) return null;
            return prettyPrintDocumentType(value);
          }
        : undefined,
    },
    {
      field: showAdvancedTooltips ? "activities" : "activity",
      headerName: "Activity",
      flex: showAdvancedTooltips ? 1.5 : 1,
      headerClassName: showAdvancedTooltips ? "wrapHeader" : undefined,
      ...(showAdvancedTooltips
        ? {
            renderCell: ({ value }) => {
              if (!value?.length) return null;
              const activity = value[0];
              const title = getStaffFacingActivityTitle(activity);
              return (
                <Tooltip title={title}>
                  <span
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "block",
                    }}
                  >
                    {title}
                  </span>
                </Tooltip>
              );
            },
          }
        : {
            valueGetter: (_, row) =>
              row.activities?.length > 0
                ? getStaffFacingActivityTitle(row.activities[0])
                : null,
          }),
    },
    {
      field: "actions",
      type: "actions",
      resizable: false,
      sortable: false,
      getActions: ({ row }) => [
        <Tooltip title="Download Document" key={2}>
          <GridActionsCellItem
            onClick={() => {
              getDocumentDownloadLink({
                variables: { id: row.id },
              });
            }}
            key={2}
            label="Download Document"
            icon={<Download />}
          />
        </Tooltip>,
        <Tooltip title="Edit Document" key={0}>
          <GridActionsCellItem
            onClick={() =>
              setAddEditDialogState((state) => ({
                ...state,
                open: true,
                mode: EditMode.EDIT,
                step: FormState.FORM,
                documentId: row.id,
                title: row.title,
                documentType: row.documentType,
                fileExtension: row.fileExtension,
                documentTags: row.documentTags ?? undefined,
                activityId: row.activities?.[0]?.id ?? null,
              }))
            }
            key={2}
            label="Edit Document"
            icon={<Edit />}
          />
        </Tooltip>,
        <Tooltip title="Delete Document" key={1}>
          <GridActionsCellItem
            onClick={() => setDeleteDocumentId(row.id)}
            key={1}
            label="Delete Document"
            icon={<DeleteIcon />}
          />
        </Tooltip>,
      ],
    },
  ];

  const content = (
    <>
      {title ? (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography variant={showPaper ? "h6" : "h5"}>{title}</Typography>
          <Button
            variant="contained"
            disabled={loading}
            onClick={() =>
              setAddEditDialogState((state) => ({
                ...state,
                open: true,
                mode: EditMode.ADD,
                activityId: activities?.[0]?.id ?? undefined,
              }))
            }
          >
            Add Document
          </Button>
        </Box>
      ) : (
        <Box display="flex" justifyContent="flex-end" sx={{ mb: 2 }}>
          <Button
            variant="contained"
            disabled={loading}
            onClick={() =>
              setAddEditDialogState((state) => ({
                ...state,
                open: true,
                mode: EditMode.ADD,
                activityId: activities?.[0]?.id ?? undefined,
              }))
            }
          >
            Add Document
          </Button>
        </Box>
      )}
      <Stack spacing={2}>
        <Box>
          <DataGrid
            sx={{
              width: "100%",
              height: showAdvancedTooltips ? 300 : undefined,
              "& .MuiDataGrid-virtualScroller": {
                minHeight: "50px",
              },
              "& .wrapHeader": {
                lineHeight: "1rem",
              },
              "& .wrapHeader .MuiDataGrid-columnHeaderTitleContainer": {
                whiteSpace: "normal",
              },
              "& .wrapHeader .MuiDataGrid-colCellTitle, .wrapHeader .MuiDataGrid-columnHeaderTitle":
                {
                  whiteSpace: "normal",
                },
            }}
            hideFooter
            disableRowSelectionOnClick
            initialState={{
              pinnedColumns: {
                right: ["actions"],
              },
            }}
            onRowClick={({ row }) => {
              setAddEditDialogState((state) => ({
                ...state,
                open: true,
                mode: EditMode.EDIT,
                step: FormState.FORM,
                documentId: row.id,
                title: row.title,
                documentType: row.documentType,
                fileExtension: row.fileExtension,
                documentTags: row.documentTags ?? undefined,
                activityId: row.activities?.[0]?.id ?? null,
              }));
            }}
            rows={documents}
            columns={columns}
          />
        </Box>
        {addEditDialogState.open && (reportId || tenantId) && (
          <AddEditDocumentDialog
            dialogState={addEditDialogState}
            setDialogState={setAddEditDialogState}
            reportId={reportId}
            tenantId={tenantId}
            generateTitleOnCreate={true}
            activityPickerOptions={activities?.map((x) => ({
              id: x.id,
              title: getStaffFacingActivityTitle(x),
            }))}
            onSubmit={() => {
              handleRefetch();
            }}
            refetchQueries={refetchQueriesList}
          />
        )}
        {deleteDocumentId && (
          <ConfirmDeleteDocuments
            deleteDocument={async () => {
              await deleteDocument({ variables: { id: deleteDocumentId } });
            }}
            onClose={() => setDeleteDocumentId(null)}
            deleting={deleting}
          />
        )}
      </Stack>
    </>
  );

  if (showPaper) {
    return (
      <Stack py={3} component={Paper} elevation={0} sx={{ borderRadius: 2 }}>
        <Box sx={{ px: 2 }}>{content}</Box>
      </Stack>
    );
  }

  return content;
}
